import apiClient from './api';

export interface WalletSessionCreateRequest {
  wallet_address: string;
  signed_message: string;
  signature: string;
  user_agent?: string;
}

export interface WalletSessionResponse {
  session_token: string;
  wallet_address: string;
  expires_at: string;
  is_active: boolean;
  created_at: string;
  last_activity: string;
}

export interface WalletSessionStatusResponse {
  is_authorized: boolean;
  wallet_address?: string;
  expires_at?: string;
  session_token?: string;
}

export interface AuthMessageResponse {
  message: string;
  timestamp: number;
  wallet_address: string;
}

class WalletSessionService {
  private sessionToken: string | null = null;

  /**
   * Get the message to sign for wallet authentication
   */
  async getAuthMessage(walletAddress: string): Promise<AuthMessageResponse> {
    try {
      const response = await apiClient.get<AuthMessageResponse>(
        `/wallet/auth/message?wallet_address=${walletAddress}`
      );
      return response.data;
    } catch (error) {
      console.error('Error getting auth message:', error);
      throw new Error('Failed to get authentication message');
    }
  }

  /**
   * Create a new wallet session with signed message
   */
  async createSession(request: WalletSessionCreateRequest): Promise<WalletSessionResponse> {
    try {
      const response = await apiClient.post<WalletSessionResponse>(
        '/wallet/auth/session',
        {
          ...request,
          user_agent: request.user_agent || navigator.userAgent,
        }
      );

      // Store session token
      this.sessionToken = response.data.session_token;
      this.saveSessionToken(response.data.session_token);

      return response.data;
    } catch (error: any) {
      console.error('Error creating session:', error);
      
      // Handle specific error types
      if (error.response?.status === 400) {
        const errorDetail = error.response.data?.detail;
        if (typeof errorDetail === 'object' && errorDetail.error === 'SIGNATURE_VERIFICATION_FAILED') {
          throw new Error('Signature verification failed. Please try signing again.');
        }
        throw new Error(errorDetail?.message || 'Invalid session request');
      }
      
      throw new Error('Failed to create wallet session');
    }
  }

  /**
   * Validate a session token
   */
  async validateSession(sessionToken?: string): Promise<WalletSessionStatusResponse> {
    const tokenToValidate = sessionToken || this.sessionToken;
    
    if (!tokenToValidate) {
      return { is_authorized: false };
    }

    try {
      const response = await apiClient.get<WalletSessionStatusResponse>(
        `/wallet/auth/session/${tokenToValidate}`
      );
      
      // Update stored token if validation was successful
      if (response.data.is_authorized && response.data.session_token) {
        this.sessionToken = response.data.session_token;
        this.saveSessionToken(response.data.session_token);
      }

      return response.data;
    } catch (error) {
      console.error('Error validating session:', error);
      // If validation fails, clear the stored token
      this.clearSession();
      return { is_authorized: false };
    }
  }

  /**
   * Check wallet authorization status by address
   */
  async checkWalletAuthorization(walletAddress: string): Promise<WalletSessionStatusResponse> {
    try {
      const response = await apiClient.get<WalletSessionStatusResponse>(
        `/wallet/auth/status/${walletAddress}`
      );
      
      // Update stored token if authorized
      if (response.data.is_authorized && response.data.session_token) {
        this.sessionToken = response.data.session_token;
        this.saveSessionToken(response.data.session_token);
      }

      return response.data;
    } catch (error) {
      console.error('Error checking wallet authorization:', error);
      return { is_authorized: false };
    }
  }

  /**
   * Revoke the current session
   */
  async revokeSession(sessionToken?: string): Promise<void> {
    const tokenToRevoke = sessionToken || this.sessionToken;
    
    if (!tokenToRevoke) {
      return;
    }

    try {
      await apiClient.delete(`/wallet/auth/session/${tokenToRevoke}`);
    } catch (error) {
      console.error('Error revoking session:', error);
      // Continue with local cleanup even if API call fails
    } finally {
      this.clearSession();
    }
  }

  /**
   * Revoke all sessions for a wallet address
   */
  async revokeWalletSessions(walletAddress: string): Promise<void> {
    try {
      await apiClient.delete(`/wallet/auth/wallet/${walletAddress}`);
    } catch (error) {
      console.error('Error revoking wallet sessions:', error);
      throw new Error('Failed to revoke wallet sessions');
    } finally {
      this.clearSession();
    }
  }

  /**
   * Get current session token
   */
  getSessionToken(): string | null {
    return this.sessionToken || this.loadSessionToken();
  }

  /**
   * Check if we have a valid session token stored
   */
  hasSessionToken(): boolean {
    return !!(this.sessionToken || this.loadSessionToken());
  }

  /**
   * Clear session from memory and storage
   */
  clearSession(): void {
    this.sessionToken = null;
    localStorage.removeItem('wallet_session_token');
    sessionStorage.removeItem('wallet_session_token');
  }

  /**
   * Save session token to storage
   */
  private saveSessionToken(token: string): void {
    // Use localStorage for session persistence across browser sessions
    localStorage.setItem('wallet_session_token', token);
  }

  /**
   * Load session token from storage
   */
  private loadSessionToken(): string | null {
    return localStorage.getItem('wallet_session_token');
  }

  /**
   * Initialize session token from storage on service creation
   */
  initialize(): void {
    this.sessionToken = this.loadSessionToken();
  }
}

// Create singleton instance
export const walletSessionService = new WalletSessionService();

// Initialize on module load
walletSessionService.initialize(); 